import { useMemo, useState } from 'react';
import {
    Marker,
    useMap,
    useMapEvents
} from 'react-leaflet';

import L from 'leaflet';

import getDistanceInMeters from '../../utils/map/getDistanceInMeters';

import { MAP_COLORS } from '../../utils/map/constants';

function getBearing(from, to) {

    const lat1 =
        Number(from.latitude) * Math.PI / 180;

    const lat2 =
        Number(to.latitude) * Math.PI / 180;

    const deltaLng =
        (
            Number(to.longitude) -
            Number(from.longitude)
        ) * Math.PI / 180;

    const y =
        Math.sin(deltaLng) *
        Math.cos(lat2);

    const x =
        Math.cos(lat1) * Math.sin(lat2) -
        Math.sin(lat1) *
        Math.cos(lat2) *
        Math.cos(deltaLng);

    const bearing =
        Math.atan2(y, x) *
        180 / Math.PI;

    return (bearing + 360) % 360;
}

function createDirectionIcon(angle, size) {

    const fontSize = Math.round(size * 0.96);
    const arrowShadow = MAP_COLORS.realRoute;

    return L.divIcon({
        className: '',
        html: `
            <div
                style="
                    width:${size}px;
                    height:${size}px;
                    display:flex;
                    align-items:center;
                    justify-content:center;
                    transform:rotate(${angle}deg);
                    font-size:${fontSize}px;
                    font-weight:bold;
                    color:white;
                    text-shadow:
                        -1px -1px 2px ${arrowShadow},
                         1px -1px 2px ${arrowShadow},
                        -1px  1px 2px ${arrowShadow},
                         1px  1px 2px ${arrowShadow};
                "
            >
                ↑
            </div>
        `,
        iconSize: [
            size,
            size
        ],
        iconAnchor: [
            size / 2,
            size / 2
        ]
    });
}

function getArrowDistanceByZoom(zoom) {
    
    if (zoom >= 18) return 200;
    if (zoom >= 17) return 250;
    if (zoom >= 16) return 300;
    if (zoom >= 15) return 400;
    if (zoom >= 14) return 500;
    if (zoom >= 13) return 750;
    if (zoom >= 12) return 1000;

    return 1500;
}

function getArrowSizeByZoom(zoom) {

    if (zoom >= 18) return 60;
    if (zoom >= 17) return 50;
    if (zoom >= 16) return 46;
    if (zoom >= 15) return 44;
    if (zoom >= 14) return 36;
    if (zoom >= 13) return 32;
    if (zoom >= 12) return 24;

    return 18;
}

export default function RealRouteDirection({
    realPositions
}) {

    const map = useMap();

    const [zoom, setZoom] = useState(
        map.getZoom()
    );

    useMapEvents({
        zoomend: () => {
            setZoom(map.getZoom());
        }
    });

    const arrows = useMemo(() => {

        if (!realPositions?.length) {
            return [];
        }

        const minDistance = getArrowDistanceByZoom(zoom);

        const result = [];

        let accumulatedDistance = 0;

        for (
            let i = 0;
            i < realPositions.length - 1;
            i++
        ) {

            const from = realPositions[i];
            const to = realPositions[i + 1];

            const segmentDistance =
                getDistanceInMeters(
                    from.latitude,
                    from.longitude,
                    to.latitude,
                    to.longitude
                );

            accumulatedDistance +=
                segmentDistance;

            if (
                accumulatedDistance <
                minDistance
            ) {
                continue;
            }

            const latitude =
                (
                    Number(from.latitude) +
                    Number(to.latitude)
                ) / 2;

            const longitude =
                (
                    Number(from.longitude) +
                    Number(to.longitude)
                ) / 2;

            const angle =
                getBearing(from, to);

            result.push({
                latitude,
                longitude,
                angle,
                index: i
            });

            accumulatedDistance = 0;
        }

        return result;

    }, [realPositions, zoom]);

    const arrowSize = getArrowSizeByZoom(zoom);

    return (
        <>
            {arrows.map(arrow => (
                <Marker
                    key={arrow.index}
                    position={[
                        arrow.latitude,
                        arrow.longitude
                    ]}
                    icon={createDirectionIcon(
                        arrow.angle,
                        arrowSize
                    )}
                    interactive={false}
                />
            ))}
        </>
    );
}